import { clsx } from "clsx";
import { IconFileInvoice, IconShoppingCart } from "@tabler/icons-react";
import type { PricelistItemDto } from "../../../../api";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { GatedPrice } from "../../../../components/common/gated-price";
import { Heading, Text } from "../../../../components/common/typography";
import { Select } from "../../../../components/forms/select";
import { useCartUiStore } from "../../../../hooks/stores/useCartUiStore";
import { ProductPurchaseTerms } from "./product-purchase-terms";

const QUANTITY_OPTIONS = [1, 5, 10, 25, 50, 100];

function formatTierRange(tier: PricelistItemDto, next?: PricelistItemDto) {
  const min = tier.minQuantity ?? 1;
  const nextMin = next?.minQuantity;
  if (nextMin && nextMin > min) return `${min}-${nextMin - 1} units`;
  return `${min}+ units`;
}

interface ProductPricingPanelProps {
  currentPrice: number;
  strikePrice: number | null;
  discountPercentage?: number;
  sortedTiers: PricelistItemDto[];
  activeTier: PricelistItemDto | null;
  quantity: number;
  onQuantityChange: (qty: number) => void;
  onBuyNow?: () => void;
  onAddToBasket?: () => void;
  onRequestQuotation?: () => void;
  isRequestingQuotation?: boolean;
  displayImage?: string | null;
  leadTimeDays?: number | null;
  onAskAboutDelivery?: () => void;
}

export function ProductPricingPanel({
  currentPrice,
  strikePrice,
  discountPercentage,
  sortedTiers,
  activeTier,
  quantity,
  onQuantityChange,
  onBuyNow,
  onAddToBasket,
  onRequestQuotation,
  isRequestingQuotation,
  displayImage,
  leadTimeDays,
  onAskAboutDelivery,
}: ProductPricingPanelProps) {
  const playFlyToCart = useCartUiStore((s) => s.playFlyToCart);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-2xl bg-bgLight p-5">
        {strikePrice != null && strikePrice > currentPrice ? (
          <div className="flex items-center gap-2">
            <Text variant="bodySm" tone="dark" inline className="line-through">
              <GatedPrice>QAR {strikePrice.toLocaleString()}</GatedPrice>
            </Text>
            {discountPercentage != null && discountPercentage > 0 && (
              <Chip variant="secondary">{discountPercentage}% off</Chip>
            )}
          </div>
        ) : null}

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
          <Heading variant="h2" tone="dark"><GatedPrice>QAR {currentPrice.toLocaleString()}</GatedPrice></Heading>
          <Text variant="bodySm" tone="dark" inline>/ unit</Text>
        </div>
        <Text variant="bodySm" tone="dark">Excl. VAT</Text>

        {sortedTiers.length > 0 ? (
          <div className="flex flex-col gap-3 border-t border-borderDefault pt-4">
            <Text variant="bodyBold" tone="dark">Bulk Pricing</Text>
            <div className="grid grid-cols-3 gap-2">
              {sortedTiers.slice(0, 3).map((tier, i) => {
                const active = tier === activeTier;
                const basePrice = sortedTiers[0].fixedPrice ?? 0;
                const tierPrice = tier.fixedPrice ?? 0;
                const savings = basePrice > 0 && i > 0
                  ? Math.round(((basePrice - tierPrice) / basePrice) * 100)
                  : 0;
                return (
                  <button
                    key={tier.id ?? i}
                    type="button"
                    onClick={() => onQuantityChange(tier.minQuantity ?? 1)}
                    className={clsx(
                      "relative flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl border px-1 py-3 text-center transition-colors",
                      active ? "border-textDark bg-textDark text-white" : "border-borderDefault bg-bgWhite text-textDark",
                    )}
                  >
                    <Text variant="bodyXsBold" inline className={active ? "text-white" : "text-textDark"}>
                      {formatTierRange(tier, sortedTiers[i + 1])}
                    </Text>
                    <Text variant="bodyXs" inline className={active ? "text-white/90" : "text-textBody"}>
                      <GatedPrice>QAR {tierPrice.toLocaleString()}</GatedPrice>
                    </Text>
                    {savings > 0 && (
                      <span className={clsx(
                        "absolute -top-2 left-1/2 -translate-x-1/2 rounded-full px-2 py-0.5 text-[11px] font-bold leading-normal whitespace-nowrap",
                        active ? "bg-accentGreenDark text-white" : "bg-accentGreenTintBg text-accentGreenDark",
                      )}>
                        -{savings}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-3">
          <Text variant="bodyBold" tone="dark">Quantity</Text>
          <Select
            name="product-quantity"
            value={String(quantity)}
            onChange={(e) => onQuantityChange(Number(e.target.value))}
            options={QUANTITY_OPTIONS.map((opt) => ({ value: String(opt), label: String(opt) }))}
            density="compact"
            className="min-w-[120px]"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button variant="primary" size="sm" onClick={onBuyNow}>Buy now</Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<IconShoppingCart size={18} />}
            onClick={(e) => {
              onAddToBasket?.();
              playFlyToCart({ source: e.currentTarget, image: displayImage ?? undefined });
            }}
          >
            Add to cart
          </Button>
        </div>

        {onRequestQuotation ? (
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            leftIcon={<IconFileInvoice size={18} />}
            onClick={onRequestQuotation}
            disabled={isRequestingQuotation}
          >
            {isRequestingQuotation ? "Creating quotation…" : "Request quotation"}
          </Button>
        ) : null}
      </div>

      <ProductPurchaseTerms leadTimeDays={leadTimeDays} onAskAboutDelivery={onAskAboutDelivery} />
    </div>
  );
}
